/**
 * Root route component: gates on the identity session, boots the shell
 * session, then mounts the providers every screen relies on (session,
 * office scope, live updates) and the frame. Sign-in pages render bare,
 * without the shell around them.
 */
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { SUPPORT_ADMIN_PATH } from '@velora/contracts/identity';
import { ApiError, getShellBootstrap } from '@velora/sdk';
import { useEffect, useMemo } from 'react';
import { AuthGate, useSessionContext } from '../../modules/identity/index.js';
import { api } from '../api.js';
import { isDemoMode } from '../demo/mode.js';
import {
  IdentityBridge,
  personScopeParam,
  SHELL_BOOTSTRAP_KEY,
} from '../identity/IdentityBridge.js';
import { LiveProvider } from '../live/LiveProvider.js';
import { HubBridge } from '../hr/HubBridge.js';
import { FlowNotifier } from '../notifications/FlowNotifier.js';
import { KpiAlertNotifier } from '../notifications/KpiAlertNotifier.js';
import PromptNavBadges from '../home/dashboard/PromptNavBadges.js';
import { TxApprovalNotifier } from '../notifications/TxApprovalNotifier.js';
import type { WebModule } from '../module.js';
import { OfficeScopeProvider } from '../office/OfficeScope.js';
import { usePrefs } from '../prefs/prefs.js';
import { SessionProvider } from '../session/session.js';
import { AndroidNavigation } from '../android/AndroidHost.js';
import { DesktopNavigation } from '../desktop/DesktopNavigation.js';
import { PopoutFrame } from '../windows/PopoutFrame.js';
import { readPopout } from '../windows/popout.js';
import { isBarePath, isChromelessPath } from './barePaths.js';
import { ShellFrame } from './ShellFrame.js';
import { BootError, BootSplash, NoOffices, SignedOut } from './states.js';

export { BARE_PATH_PREFIXES, isBarePath } from './barePaths.js';

export const BOOTSTRAP_KEY = SHELL_BOOTSTRAP_KEY;

/**
 * Sign-in pages render bare. Everything else sits behind identity's
 * `AuthGate` (sign-in, two-step, lock screen, idle lock, support banner),
 * and the shell boots only once the session is active.
 */
export function ShellRoot({ modules }: { modules: readonly WebModule[] }) {
  const { pathname } = useLocation();
  if (isBarePath(pathname)) return <Outlet />;
  return (
    <AuthGate>
      <BootedShell modules={modules} />
    </AuthGate>
  );
}

function BootedShell({ modules }: { modules: readonly WebModule[] }) {
  const { pathname, search } = useLocation();
  // A pop-out window (`?pop=`, windows/popout.ts): the page under a compact header.
  const popout = readPopout(search as Record<string, unknown>);
  const prefs = usePrefs();
  const { adopt, setSkinContext } = prefs;
  const { state } = useSessionContext();
  const personScope = personScopeParam(state);
  const boot = useQuery({
    queryKey: BOOTSTRAP_KEY,
    queryFn: ({ signal }) => getShellBootstrap(api, { signal }),
    staleTime: 5 * 60_000,
    retry: (count, err) =>
      !(err instanceof ApiError && (err.status === 401 || err.status === 403)) && count < 2,
  });

  useEffect(() => {
    if (boot.data) adopt(boot.data.preferences, true);
  }, [boot.data, adopt]);

  // An employee with no saved mode gets the practice default, else the mode
  // of their home office's PMS (theme spec 7.2). Fixed per session: switching
  // offices never flips the interface under someone's hands.
  useEffect(() => {
    const d = boot.data;
    if (!d) return;
    const home = d.offices.find((o) => o.id === d.defaultLocationId) ?? d.offices[0];
    setSkinContext({
      practiceDefault: d.skinDefaults?.defaultSkin ?? null,
      officePms: home?.pmsSystem ?? null,
    });
  }, [boot.data, setSkinContext]);

  const loginPath = useMemo(
    () => modules.flatMap((m) => m.manifest.routes).find((r) => r.path === '/login')?.path,
    [modules],
  );

  if (boot.isPending) return <BootSplash />;
  if (boot.isError) {
    if (boot.error instanceof ApiError && boot.error.status === 401) {
      return <SignedOut loginPath={loginPath} />;
    }
    const msg =
      boot.error instanceof ApiError
        ? `The server answered ${boot.error.status}${boot.error.requestId ? ` (request ${boot.error.requestId})` : ''}.`
        : 'The server could not be reached. Check the connection and try again.';
    return <BootError message={msg} onRetry={() => void boot.refetch()} />;
  }
  const session = boot.data;
  if (session.offices.length === 0) {
    // Computers2Go support accounts have no office of their own: their home
    // is the C2G Admin console.
    const who = state.kind === 'active' ? state.session : null;
    if (who?.user.isSupport && !who.impersonator)
      return <Navigate to={SUPPORT_ADMIN_PATH} replace />;
    return <NoOffices userName={session.user.displayName} />;
  }

  return (
    <SessionProvider value={session}>
      <OfficeScopeProvider session={session} personScope={personScope}>
        <LiveProvider enabled={!isDemoMode()}>
          <IdentityBridge />
          {popout ? (
            // Notifications, badges and the desktop bridge stay with the main
            // window: a pop-out would only repeat them.
            <PopoutFrame popout={popout} />
          ) : (
            <>
              <DesktopNavigation />
              <AndroidNavigation />
              <HubBridge />
              {/* Patient flow pings on every page, not only the Light Board (NEW-WF-06). */}
              <FlowNotifier />
              {/* KPI alerts in the bell, in-app first (owner research P0-2). */}
              <KpiAlertNotifier />
              {/* Phones / Leads / Messages nav badges from the Home prompt counts. */}
              <PromptNavBadges />
              {/* Treatment-plan approval requests and answers (TC role test fix 3). */}
              <TxApprovalNotifier />
              {isChromelessPath(pathname) ? <Outlet /> : <ShellFrame modules={modules} />}
            </>
          )}
        </LiveProvider>
      </OfficeScopeProvider>
    </SessionProvider>
  );
}
